/**
 * Block för den långvariga sökaren: någon som jämför leverantörer i veckor
 * innan de hör av sig. Ger konkreta svar direkt på sidan och mjuka nästa steg
 * som inte kräver ett möte.
 */

export interface ResearchStep {
  q: string;
  a: string;
}

export interface ResearchLink {
  label: string;
  href: string;
  note?: string;
}

export interface ResearchBlockProps {
  title?: string;
  intro: string;
  steps: ResearchStep[];
  links: ResearchLink[];
  /** Språk för blockets fasta texter (rubrik, stegetikett, avslutning). */
  lang?: "sv" | "en";
}

export function ResearchBlock({ title, intro, steps, links, lang = "sv" }: ResearchBlockProps) {
  const en = lang === "en";
  const heading = title ?? (en ? "Still comparing suppliers?" : "Är ni fortfarande i jämförelsefasen?");
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h3 className="text-lg font-medium">{heading}</h3>
        <p className="max-w-2xl text-white/70">{intro}</p>
      </div>

      <ol className="grid gap-3 md:grid-cols-3">
        {steps.map((s, i) => (
          <li key={s.q} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <div className="mb-2 text-xs text-mint/80">{en ? "Step" : "Steg"} {i + 1}</div>
            <div className="mb-1 font-medium">{s.q}</div>
            <p className="text-sm text-white/70">{s.a}</p>
          </li>
        ))}
      </ol>

      <div className="flex flex-wrap gap-2">
        {links.map((l) => (
          <a
            key={l.href}
            href={l.href}
            className="rounded-full border border-white/15 bg-white/[0.03] px-4 py-2 text-sm hover:border-mint/50 hover:text-mint"
            title={l.note}
          >
            {l.label}
          </a>
        ))}
      </div>

      <p className="text-sm text-white/50">
        {en ? "Not ready for a call? Email " : "Inte redo för möte? Skriv till "}
        <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="mailto:hey@fenny.se">
          hey@fenny.se
        </a>
        {en
          ? " with two lines about the project and you get a price range and a timeline back — no sales call."
          : " med två rader om uppdraget, så får ni ett prisspann och en tidsplan tillbaka — utan säljsamtal."}
      </p>
    </div>
  );
}
